/**
 * The landing page's live demo.
 *
 * A separate, tiny bundle from `app.ts` on purpose. The public page must not carry chat, the API
 * client, or anything else that assumes a session — and keeping it a distinct entry point makes
 * that a build fact rather than a rule someone has to remember.
 *
 * **This is the product, not a replica.** It mounts the real `push-and-friction` sim and the real
 * `choice` block, so what a parent drags on the landing page is the same code their child would
 * use. A hand-written imitation would drift from the real thing within a release, and would be a
 * quiet lie in the one place we are asking to be believed.
 *
 * Telemetry rides along inside the sim's SVG helpers, and is inert here: `track()` returns
 * immediately unless `initTelemetry()` has been called, which this bundle never does. So no
 * events are queued and nothing is posted to /api — which matters, because a visitor to this page
 * has no session and every such request would 401.
 *
 * Progressive enhancement throughout. The server sends a static picture of a lesson; this script
 * replaces the three interactive regions with live ones. If it fails to load, the picture stays,
 * and the page is exactly what it was before — which is why the static markup shows the answer
 * already marked rather than offering controls that would do nothing.
 */

import { append, el } from "./dom.js";
import { createPushAndFriction } from "./sims/push-and-friction.js";
import type { Sim } from "./sims/index.js";
import { renderChoice } from "./renderer/blocks/choice.js";
import type { RenderContext } from "./renderer/context.js";
import type { ChoiceBlock } from "../lesson/spec.js";

/** Where the slider starts, and what the static picture is drawn to match. */
const START_FRICTION = 0.4;

/**
 * The demo question.
 *
 * Shaped as a real `ChoiceBlock` because it is handed to the real renderer. Both wrong answers
 * carry the misconception tag the product would record — `energy-as-fuel` and `animism` are the
 * two this question actually elicits — so the page is demonstrating the diagnosis it claims to do
 * further down, not just a quiz.
 */
const QUESTION: ChoiceBlock = {
  type: "choice",
  id: "why-does-it-stop",
  prompt: "So why does it stop?",
  options: [
    {
      id: "runs-out",
      label: "The box runs out of energy inside it",
      misconception: "energy-as-fuel",
      feedback: "Not quite — energy isn't fuel stored in the box. Have another go.",
    },
    {
      id: "ground-pushes",
      label: "The rough ground pushes back on it",
      correct: true,
      feedback: "That's it. The ground pushes back, and that push is friction.",
    },
    {
      id: "tired",
      label: "Boxes get tired",
      misconception: "animism",
      feedback: "Not quite — a box can't get tired. Something outside it is pushing back.",
    },
  ],
};

/**
 * The renderer's context, stubbed.
 *
 * `choice` only reaches for `answered`, and there is nothing on this page to report to: no lesson,
 * no student, no progress model. The rest exist to satisfy the interface. `reducedMotion` is read
 * honestly rather than hardcoded, because it is the one field that changes what a visitor sees.
 */
function stubContext(): RenderContext {
  return {
    ask: () => false,
    buildPills: () => el("ul"),
    lessonOrigin: "",
    reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    answered: () => {},
  };
}

/**
 * The Smooth ↔ Rough control, wired the way a lesson wires one.
 *
 * A real `<input type="range">` rather than the static bar the server renders, and it drives the
 * sim through `setParam` — the same call the product's `slider` block makes. Built here rather
 * than sent in the HTML so that a visitor whose JavaScript never arrives is not left with a
 * control that slides and changes nothing.
 */
function frictionSlider(sim: Sim): HTMLElement {
  // `.slider` is the product's own range component, track and thumb already styled for a child's
  // finger. Reused rather than restyled, for the same reason the sim itself is.
  const input = el("input", {
    class: "slider",
    attrs: {
      type: "range",
      min: "0",
      max: "1",
      step: "0.05",
      value: String(START_FRICTION),
      "aria-label": "How rough the ground is",
    },
  });

  input.addEventListener("input", () => sim.setParam("friction", Number(input.value)));

  return el("div", { class: "home-control" }, [
    el("span", { class: "home-control-label", text: "Smooth" }),
    input,
    el("span", { class: "home-control-label", text: "Rough" }),
  ]);
}

function mount(): void {
  const demo = document.querySelector("[data-home-demo]");
  if (!demo) return;

  const simHost = demo.querySelector<HTMLElement>("[data-home-sim]");
  const controlHost = demo.querySelector<HTMLElement>("[data-home-control]");
  const quizHost = demo.querySelector<HTMLElement>("[data-home-quiz]");
  // All or nothing. A half-upgraded card — a live sim above a painted slider that does not move
  // it — is worse than the picture we were already showing.
  if (!simHost || !controlHost || !quizHost) return;

  simHost.replaceChildren();
  const sim = createPushAndFriction(
    {
      friction: START_FRICTION,
      mass: 3,
      surface: "wooden floor",
      object: "box",
      // Off here only. The meter is the best thing in this sim, but it belongs in a lesson with
      // room for it, not in a hero card that has one job.
      showEnergyMeter: false,
    },
    simHost,
  );

  controlHost.replaceChildren();
  controlHost.appendChild(frictionSlider(sim));

  quizHost.replaceChildren();
  append(quizHost, [renderChoice(QUESTION, stubContext())]);
}

mount();
